import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';
import { FormatService } from '@vk-hr-tek/core/format';

import { EventListItem as EventListEntity } from '@app/gen/events';

import { EventsDetailMapper, EventsListMapper, EventsMapper } from '../mappers';

import { EventsWithRootState as RootState } from './events.state';

export const selectFullEvents = createSelector(
  [(state: RootState) => state.events],
  (eventsState) => {
    const { entities, currentIds, parentEventTags, total } = eventsState;
    const events = currentIds
      .map((id) => entities[id])
      .filter((x): x is EventListEntity => x !== undefined);

    return { events, total, eventTags: parentEventTags };
  },
);

export const selectEvents = createSelector(
  [
    (state: RootState) => state.events.currentIds,
    (state: RootState) => state.events.entities,
    (_: RootState, inject: Inject) => inject(EventsListMapper),
  ],
  (currentIds, entities, format) => {
    return currentIds
      .map((id) => entities[id])
      .filter((x): x is EventListEntity => x !== undefined)
      .map((event) => format.processListEntity({ ...event }));
  },
);

export const selectAbleToPrintEventsByIds = createSelector(
  [
    (state: RootState) => state.events.currentIds,
    (state: RootState) => state.events.entities,
    (_: RootState, inject: Inject, ids: string[]) => ids,
  ],
  (currentIds, entities, ids) =>
    ids.some(
      (selectedId) =>
        !!currentIds
          .map((id) => entities[id])
          ?.find((eventItem) => eventItem?.id === selectedId)?.documents.length,
    ),
);

export const selectAbsencesEventsList = createSelector(
  [
    (state: RootState) => state.events.currentIds,
    (state: RootState) => state.events.entities,
    (_: RootState, inject: Inject) => inject(EventsListMapper),
  ],
  (currentIds, entities, format) => {
    return currentIds
      .map((id) => entities[id])
      .filter((x): x is EventListEntity => x !== undefined)
      .map((event) => format.processAbsencesListEntity({ ...event }));
  },
);

export const selectTotal = (state: RootState) => state.events.total;

export const selectTotalDocuments = (state: RootState) =>
  state.events.totalDocuments;

export const selectTotalMy = (state: RootState, inject?: Inject) => {
  const format = (inject as unknown as Inject)(FormatService);

  return state.events.totalMy ? format.convertNumber(state.events.totalMy) : '';
};

export const selectTotalMyTeam = createSelector(
  [
    (state: RootState) => state.events.totalMyTeam,
    (_: RootState, inject: Inject) => inject(FormatService),
  ],
  (totalMyTeam, format) => totalMyTeam && format.convertNumber(totalMyTeam),
);

export const selectTags = (state: RootState) => state.events.parentEventTags;

export const selectFinished = (state: RootState) => state.events.finished;

export const selectCompanyFilters = createSelector(
  [
    (state: RootState) => state.events.filters,
    (_: RootState, inject: Inject) => inject(EventsMapper),
  ],
  (filters, format) =>
    filters.data && format.processCompanyListFilters(filters.data),
);

export const selectEmployeeFilters = createSelector(
  [
    (state: RootState) => state.events.filters,
    (_: RootState, inject: Inject) => inject(EventsMapper),
  ],
  (filters, format) => filters.data && format.processListFilters(filters.data),
);

export const selectCancelReasons = (state: RootState) =>
  state.events.cancelReasons;

export const selectPepSignIsSubmittingStatus = (state: RootState) => {
  return state.events.pepSign.status === 'loading';
};

export const selectPepSignBatchIsSubmittingStatus = (state: RootState) => {
  return state.events.pepSignBatch.status === 'loading';
};

export const selectUkepSignIsSubmittingStatus = (state: RootState) => {
  return state.events.ukepSign.status === 'loading';
};

export const selectUkepSignBatchIsSubmittingStatus = (state: RootState) => {
  return state.events.ukepSignBatch.status === 'loading';
};

export const selectUnepSignIsSubmittingStatus = (state: RootState) => {
  return state.events.unepSign.status === 'loading';
};

export const selectUnepSignBatchIsSubmittingStatus = (state: RootState) => {
  return state.events.unepSignBatch.status === 'loading';
};

export const selectEventValidatorsByNodeId = createSelector(
  [
    (state: RootState) => state.events.detail,
    (_: RootState, inject: Inject, id: string) => id,
  ],
  (detail, id) => {
    return detail && detail.data && detail.data.validators
      ? detail.data.validators[id]
      : [];
  },
);

export const selectError = (state: RootState) => state.events.error;

export const selectDetailError = (state: RootState) =>
  state.events.detail.error;

export const selectStatus = (state: RootState) => state.events.status;

export const selectDetailStatus = (state: RootState) =>
  state.events.detail.status;

export const selectDetailPrintStatus = (state: RootState) =>
  state.events.detail.print.status;

export const selectDetailActionTypeForLoadingText = (state: RootState) =>
  state.events.detail.actionTypeForLoadingText;

export const selectDetailStartId = (state: RootState) =>
  state.events.detail.startEventId;

export const selectEventRaw = (state: RootState) => state.events.detail.data;

export const selectEvent = createSelector(
  [
    (state: RootState) => state.events.detail.data,
    (_: RootState, inject: Inject) => inject(EventsDetailMapper),
    (state: RootState) => state.events.competencyOptions?.items,
    (_: RootState, inject: Inject, variant?: 'event' | 'candidate') =>
      variant ?? 'event',
  ],
  (detail, format, competencyOptions, variant) => {
    return detail
      ? format.processDetailEntity(detail, variant, competencyOptions)
      : null;
  },
);

export const selectCanCreateEvent = (state: RootState) =>
  state.events.canCreateEvent;

export const selectCanCreateEventType = (state: RootState) =>
  state.events.canCreateEventType;

export const selectEventTypeOptions = (state: RootState) =>
  state.events.eventTypes.companies;

export const selectEventTypeOptionsStatus = (state: RootState) =>
  state.events.eventTypes.status;

export const selectEventTypeOptionsError = (state: RootState) =>
  state.events.eventTypes.error;

export const selectEventFlowStatus = (state: RootState) =>
  state.events.flow.status;

export const selectEventLinearFlowStatus = (state: RootState) =>
  state.events.flow.linearFlowStatus;

export const selectEventFlowTree = (state: RootState) => state.events.flow.tree;

export const selectLinearEventFlowTree = (state: RootState) =>
  state.events.flow.linearTree;

export const selectEventFlowError = (state: RootState) =>
  state.events.flow.error;

export const selectEventFlowPrintStatus = (state: RootState) =>
  state.events.printFlow.status;

export const selectShowUrgentEvents = (state: RootState) =>
  state.events.showUrgentEvents;

export const selectResendedNode = createSelector(
  [
    (state: RootState) => state.events.resendedNodes,
    (
      _: RootState,
      inject: Inject,
      { eventId }: { eventId: string; nodeId: string },
    ) => eventId,
    (
      _: RootState,
      inject: Inject,
      { nodeId }: { eventId: string; nodeId: string },
    ) => nodeId,
  ],
  (resendedNodes, eventId, nodeId) =>
    resendedNodes.find(
      (node) => node.eventId === eventId && node.nodeId === nodeId,
    ),
);

export const selectCodeRequestingError = (state: RootState) =>
  state.events.codeRequesting.error;

export const selectEventAbsences = createSelector(
  [
    (state: RootState) => state.events.detail.absences,
    (_: RootState, inject: Inject) => inject(EventsDetailMapper),
  ],
  (absences, mapper) => mapper.processAbsences(absences),
);

export const selectAvailableVacationDaysByType = (state: RootState) =>
  state.events.detail.data?.options?.available_vacation_days_by_types;
